/**
 * Voicemail.js
 * Talkroute
 *
 * Created by Vel Genov on 1/29/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Dimmer, Loader } from "semantic-ui-react";
import { push } from "connected-react-router";
import InfiniteScroll from "react-infinite-scroll-component";
import { createStyles, makeStyles } from "@material-ui/core";
import {
  fetchVoicemails,
  showVoicemailDetails,
  findVoicemailIndexById,
  fetchVoicemailFile,
  deleteVoicemail,
} from "../actions/voicemailActions";
import VoicemailLineItem from "./voicemailComponents/VoicemailLineItem";
import uiString from "../constants/uiString";
import routes from "../constants/routes";
import validateArray from "../helpers/arrayValidator";
import { domIds } from "@constants/testConstants";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import ContextMenu from "./PresentationComponents/ContextMenu";
import { useWindowSize } from "@helpers/hooks/useWindowsSize.hook";
import VoiceMessagesService from "@services/VoiceMessages.service";
import { VoicemailData, WELCOME_VOICEMAIL } from "../services/dataClasses/VoicemailData";
import ConfirmDialog from "./PresentationComponents/ConfirmDialog";
import useService from "@helpers/hooks/useService.hook";
import useVoicemailSelector from "@helpers/hooks/useVoicemailSelector.hook";
import useMetadata from "@helpers/hooks/useMetadata.hook";
import { addContactNameToVoicemail } from "@helpers/functions/contacts";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import { GET_TEN_THOUSAND_CONTACTS, CONTACTS_INITIAL_PAGE } from "./Contacts/constants";
import { getEditableForId } from "@services/dataClasses/VoicemailMailbox";

// Enables a vertical scrollbar for the voicemail line item display, when content overflows
// Hides the horizontal scrollbar at all times
// The element needs to have a specific height set, in order to show a scrollbar
const voicemailItemStyle = {
  height: "100%",
  overflowY: "auto" as any,
  overflowX: "hidden" as any,
  backgroundColor: "var(--color-background-grey)",
};

const useStyles = makeStyles((theme) =>
  createStyles({
    wrapper: {
      height: "100%",
      overflowY: "auto" as any,
      overflowX: "hidden" as any,
      borderRight: "1px solid var(--color-dark-grey)",
    },
  })
);

/**
 * Voicemail component
 */
const Voicemail: React.FC = () => {
  const callHistory = useAppSelector((state) => state.callHistory.data);
  const _filteredVoicemails = useAppSelector(
    (state) => state.voicemails.filteredData
  );
  const dataState = useAppSelector((state) => state.voicemails.dataState);
  const voicemails = useAppSelector<VoicemailData[]>((state) => state.voicemails.data);
  const filterBy = useAppSelector((state) => state.voicemails.filterBy);
  const dispatch = useDispatch();
  const classes = useStyles();
  const { height } = useWindowSize();
  const { Delete } = useService(VoiceMessagesService);
  const { markWelcomeVoicemailAsDeleted, markWelcomeVoicemailAsRead } = useMetadata();

  const {
    handleSelection,
    isItemSelected,
    isOnlyOneElementSelected,
    selectAll,
    selectItem,
  } = useVoicemailSelector(voicemails);
  const selectedItems = useAppSelector(
    (state) => state.itemSelection.selectedVoicemails
  );

  const [isEditable, setIsEditable] = useState(true);
  const mailboxes = useAppSelector((state) => state.voicemails.mailboxes);

  useEffect(() => {
    const newIsEditable = selectedItems.every((entry) => {
      const editable = getEditableForId(entry.mailboxId, mailboxes);
      return editable;
    });
    setIsEditable(newIsEditable);
  }, [selectedItems, mailboxes]);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openContextMenu, setOpenContextMenu] = useState(false);
  const [initIsComplete, setInitIsComplete] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ xPos: 0, yPos: 0 });
  const contextMenuCloseHandler = () => setOpenContextMenu(false);
  const contextMenuOpenHandler = (event: MouseEvent) => {
    event.preventDefault();
    const HORIZONTAL_POS_LIMIT = 600;
    const VERTICAL_POS_LIMIT = height - (!isOnlyOneElementSelected ? 200 : 150);
    const pos = {
      xPos: Math.min(event.pageX - 2, HORIZONTAL_POS_LIMIT),
      yPos: Math.min(event.pageY - 15, VERTICAL_POS_LIMIT),
    };
    setContextMenuPos(pos);
    setOpenContextMenu(true);
  };
  const {data: contactList} = useGetAllContactsQuery({ params: { 
    pageSize: GET_TEN_THOUSAND_CONTACTS,
    page: CONTACTS_INITIAL_PAGE
  } });

  const filteredVoicemails = useMemo(() => contactList ?
  addContactNameToVoicemail(contactList, _filteredVoicemails):
  _filteredVoicemails, [contactList, _filteredVoicemails])

  /**
   * Show the list of voicemails
   *
   * @param voicemails array containing the voicemails to show
   */
  const showVoicemail = (voicemails: VoicemailData[]) => (
    <div style={{ ...voicemailItemStyle }}>
      {showEmptyVoicemails(voicemails)}
      {createVoicemailLineItems(voicemails)}
    </div>
  );

  /**
   * Display details for a single voicemail
   *
   * @param voicemailId the ID of the voicemail to show details for
   */
  const showVoicemailDetailsCallback = (voicemailId: number | null) => {
    if (!voicemailId) {
      dispatch(showVoicemailDetails(callHistory, undefined, routes.VOICEMAIL));
      if (location.pathname !== routes.VOICEMAIL_DETAILS) {
        dispatch(push(routes.VOICEMAIL_DETAILS));
      }
    }

    const voicemailArrayIndex = findVoicemailIndexById({
      voicemails,
      voicemailId,
    });
    if (voicemailArrayIndex >= 0) {
      const voicemail = voicemails[voicemailArrayIndex];
      dispatch(showVoicemailDetails(callHistory, voicemail, routes.VOICEMAIL));
      dispatch(push(routes.VOICEMAIL_DETAILS));
    }
  };

  /**
   * Create each line item for voicemail
   *
   * @param voicemails an array containing voicemails
   */
  const createVoicemailLineItems = (voicemails: VoicemailData[]) => {
    let returnValue: any = null;

    if (Array.isArray(voicemails) && voicemails.length > 0) {
      returnValue = voicemails.map((entry) => {
        const { id, duration, mailboxId, messageRead } = entry;
        const date = String(entry.getFormattedDate());
        const phoneNumber = entry.getContactInfo();
        return (
          <VoicemailLineItem
            key={id}
            voicemailId={id}
            duration={duration}
            mailboxId={mailboxId}
            messageRead={messageRead}
            date={date}
            phoneNumber={phoneNumber}
            showDetailsCallback={showVoicemailDetailsCallback}
            handleClick={(event: MouseEvent) => handleSelection(event, entry, filteredVoicemails, () => showVoicemailDetailsCallback(entry.id))}
            handleContextMenu={event => {
                  contextMenuOpenHandler(event);
                  handleSelection(event, entry, filteredVoicemails, () => showVoicemailDetailsCallback(entry.id))
                }
              }
            isSelected={isItemSelected(entry)}
            darkColoredSelection={isOnlyOneElementSelected}
          />
        );
      });

      return returnValue;
    }
  };

  /**
   * Request to load the voicemails
   */
  const loadVoicemails = () => {
    dispatch(fetchVoicemails());
  };

  /**
   * Check if the user's voicemails are empty and return a message to be displayed if it they are
   *
   * @param voicemails an array containing voicemails
   */
  const showEmptyVoicemails = (voicemails: VoicemailData[]) => {
    if (!Array.isArray(voicemails) || !(voicemails.length > 0)) {
      return (
        <h5 style={{ textAlign: "center", margin: "13px" }}>
          {uiString.EMPTY_VOICEMAILS}
        </h5>
      );
    }
  };

  /**
   * Displays a loading voicemails message
   */
  const showLoadingMessage = () => {
    return (
      <Dimmer
        id={domIds.VOICEMAIL_LOADER}
        active
        style={{ height: "100%", zIndex: 5 }}
      >
        <Loader />
      </Dimmer>
    );
  };

  /**
   * Select the 1st voicemail, if available
   */
  const selectVoicemailByIndex = (index: number) => {
    if (validateArray(filteredVoicemails)) {
      const selectedVoicemail = filteredVoicemails[index >= 0 ? index : 0];
      if (selectedVoicemail) {
        const { id: voicemailId } = selectedVoicemail;
        showVoicemailDetailsCallback(voicemailId);
        selectItem(selectedVoicemail)
      }
    } else {
      showVoicemailDetailsCallback(null);
    }
  };

  useEffect(() => {
    loadVoicemails();
    
    if (isOnlyOneElementSelected) {
      const findIndexOfSelectedItem = voicemails.findIndex(vmail => vmail.id === selectedItems[0].id);
      selectVoicemailByIndex(findIndexOfSelectedItem);
    } else {
      const FIRST_VOICEMAIL_INDEX = 0;
      selectVoicemailByIndex(FIRST_VOICEMAIL_INDEX);
    }

    setInitIsComplete(true);
  }, []);

  useEffect(() => {
    /**
     * When first loading this component we'd like voicemail
     * selection to be handed by the initialization `useEffect` call
     */
    if (initIsComplete) {
      const FIRST_VOICEMAIL_INDEX = 0;
      selectVoicemailByIndex(FIRST_VOICEMAIL_INDEX);
    }
  }, [filterBy]);

  const voicemailsScrolled = () => {};

  const refreshVoicemails = () => {
    loadVoicemails();
  };

  const deleteMessage = async () => {
    // TODO: refactor once batch delete endpoint is available
    for (const item of selectedItems) {
      dispatch(deleteVoicemail(item.id, () => {
        if (filteredVoicemails[0].id === item.id) {
          selectVoicemailByIndex(1)
        } else {
          selectVoicemailByIndex(0)
        }
        if (item.id === 1) {
          markWelcomeVoicemailAsDeleted();
        }
      }));
    }
    // dispatch(fetchVoicemails(false, true));
    selectVoicemailByIndex(0);
    setOpenDeleteDialog(false);
  };

  const contextMenuItems = [
    {
      callback: () => {
        selectAll(filteredVoicemails);
        setOpenContextMenu(false);
      },
      render: true,
      label: "Select All Entries",
      disabled: false,
    },
    {
      callback: () => {
        selectVoicemailByIndex(0);
        setOpenContextMenu(false);
      },
      render: !isOnlyOneElementSelected,
      label: "Deselect All Entries",
      disabled: false,
    },
    {
      callback: () => {
        setOpenDeleteDialog(true);
        setOpenContextMenu(false);
      },
      render: true,
      label: "Delete",
      disabled: !isEditable,
    },
  ];

  return (
    <>
      <div id="voicemailsScroll" className={classes.wrapper}>
        <InfiniteScroll
          loader={showLoadingMessage()}
          style={{
            overflowX: "hidden",
          }}
          dataLength={filteredVoicemails.length}
          next={voicemailsScrolled}
          hasMore={false}
          scrollableTarget="voicemailsScroll"
          endMessage={null}
          refreshFunction={() => refreshVoicemails()}
          pullDownToRefresh
          pullDownToRefreshContent={
            <h3
              style={{
                textAlign: "center",
                borderRight: "1px solid var(--color-dark-grey)",
              }}
            >
              &#8595; {`${uiString.PULL_TO_REFRESH}`}
            </h3>
          }
          releaseToRefreshContent={
            <h3 style={{ textAlign: "center" }}>
              &#8593; {`${uiString.RELEASE_TO_REFRESH}`}
            </h3>
          }
        >
          {
            {
              LOADING: showLoadingMessage(),
              ERROR: showVoicemail(filteredVoicemails),
              READY: showVoicemail(filteredVoicemails),
            }[dataState]
          }
        </InfiniteScroll>
      </div>
      <ContextMenu
        closeHandler={contextMenuCloseHandler}
        items={contextMenuItems}
        open={openContextMenu}
        pos={contextMenuPos}
      />
      <ConfirmDialog
        header={uiString.VOICEMESSAGE_DELETE_CONFIRMATION_HEADER}
        content={
          !isOnlyOneElementSelected
            ? uiString.MULTIPLE_VOICEMESSAGE_DELETE_CONFIRMATION_BODY
            : uiString.VOICEMESSAGE_DELETE_CONFIRMATION_BODY
        }
        open={openDeleteDialog}
        confirmButtonLabel={uiString.DELETE}
        onCancel={() => setOpenDeleteDialog(false)}
        onConfirm={deleteMessage}
      />
    </>
  );
};

export default Voicemail;
